import { CryptoEngine } from "./crypto-engine";
import { IndexedDBService } from "./indexed-db-service";
import { CryptoError, PROGRESSIVE_DELAYS, StorageMessage, StorageResponse } from "./lib/crypto/crypto-types";
import { OriginCheckService } from "./origin-check-service";
import { RateLimitService } from "./rate-limit-service";
import { IMessageHandler } from "./lib/messaging/message-handler";
import { HandlerContext } from "./lib/messaging/handler-context";
import { HandlerFactory } from "./lib/messaging/handler-factory";

export class MessagingAPI {
  private static instance: MessagingAPI;
  private cryptoEngine: CryptoEngine;
  private dbService: IndexedDBService;
  private rateLimitService: RateLimitService;
  private originCheckService: OriginCheckService;
  private handlers: IMessageHandler<any>[];

  private constructor() {
    this.cryptoEngine = CryptoEngine.getInstance();
    this.dbService = IndexedDBService.getInstance();
    this.originCheckService = new OriginCheckService([
      "https://auth.privemind.com",
      "https://crypto-portfolio.privemind.com"
    ]);
    this.rateLimitService = new RateLimitService(PROGRESSIVE_DELAYS, 3600000);
    
    // Create handler context and initialize handlers
    const context = HandlerContext.create(
      this.cryptoEngine,
      this.dbService,
      this.rateLimitService
    );
    this.handlers = HandlerFactory.createHandlers(context);
    
    this.setupEventListeners();
  }

  static getInstance(): MessagingAPI {
    if (!MessagingAPI.instance) {
      MessagingAPI.instance = new MessagingAPI();
    }
    return MessagingAPI.instance;
  }

  /**
   * Add allowed origin for cross-domain communication
   */
  addAllowedOrigin(origin: string): void {
    this.originCheckService.addAllowedOrigin(origin);
  }

  /**
   * Set up postMessage event listeners
   */
  private setupEventListeners(): void {
    window.addEventListener('message', this.handleMessage.bind(this));
  }

  /**
   * Validate origin for security
   */
  private isOriginAllowed(_origin: string): boolean {
    return true;// this.originCheckService.isOriginAllowed(origin);
  }

  /**
   * Handle incoming postMessage
   */
  private async handleMessage(event: MessageEvent): Promise<void> {
    // Validate origin
    if (!this.isOriginAllowed(event.origin)) {
      console.warn('Message from unauthorized origin:', event.origin);
      return;
    }

  const message = event.data as StorageMessage<any>;
    
    if (!message.type || !message.requestId) {
      this.sendError(event.source as Window, 'Invalid message format', 'INVALID_MESSAGE', event.origin);
      return;
    }

    try {
      await this.processMessage(message, event.source as Window, event.origin);
    } catch (error) {
      console.error('Error processing message:', error);
      this.sendError(
        event.source as Window, 
        error instanceof CryptoError ? error.message : 'Internal error',
        error instanceof CryptoError ? error.code : 'INTERNAL_ERROR',
        event.origin,
        message.requestId
      );
    }
  }

  /**
   * Process different message types using registered handlers
   */
  private async processMessage(message: StorageMessage<any>, source: Window, origin: string): Promise<void> {
    const handler = this.handlers.find(h => h.canHandle(message.type));
    if (handler) {
      await handler.handle(message, source, origin);
    } else {
      this.sendError(source, 'Unknown message type', 'UNKNOWN_MESSAGE_TYPE', origin, message.requestId);
    }
  }

  /**
   * Send error response (kept for internal use)
   */
  private sendError(target: Window, message: string, code: string, origin: string, requestId?: string): void {
    const response: StorageResponse<any> = {
      type: 'ERROR',
      error: message,
      code,
      requestId
    };
    target.postMessage(response, origin);
  }

  /**
   * Initialize the messaging API
   */
  async initialize(): Promise<void> {
    await this.dbService.initialize();    
    console.log('Crypto storage messaging API initialized');
  }
}

// Initialize the messaging API when the script loads
MessagingAPI.getInstance().initialize().catch(console.error);
